import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Truck, Plus, Trash2, Eye, Printer, CheckCircle2, XCircle, Send, Package, FileText, Filter, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Bozza': 'bg-slate-100 text-slate-700 border-slate-300',
  'Confermata': 'bg-blue-100 text-blue-800 border-blue-300',
  'Spedita': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Annullata': 'bg-red-100 text-red-800 border-red-300',
};

export default function ShipmentRegistryPage() {
  const [shipments, setShipments] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ customer_id: '', date_from: '', date_to: '', status: '', product_id: '', customer_lot: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ customer_id: '', shipment_date: new Date().toISOString().slice(0, 10), shipment_time: '', ddt_reference: '', notes: '' });
  const [detail, setDetail] = useState(null); // full shipment with lines
  const [detailOpen, setDetailOpen] = useState(false);

  const loadShipments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api.get(`/shipment-registry?${params.toString()}`);
      setShipments(r.data || []);
    } catch (e) { toast.error('Errore caricamento spedizioni'); }
  }, [filters]);

  const loadStaticData = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.get('/clients'), api.get('/products')]);
      setClients(c.data || []);
      setProducts(p.data || []);
    } catch (e) { console.error('ShipmentRegistry: loadStaticData failed', e); }
  }, []);

  useEffect(() => { loadStaticData(); }, [loadStaticData]);
  useEffect(() => { loadShipments(); }, [loadShipments]);

  const createShipment = async (e) => {
    e.preventDefault();
    if (!createForm.customer_id || !createForm.shipment_date) { toast.error('Cliente e data obbligatori'); return; }
    try {
      const r = await api.post('/shipment-registry', createForm);
      toast.success(`Spedizione ${r.data.shipment_number} creata`);
      setCreateOpen(false);
      setCreateForm({ customer_id: '', shipment_date: new Date().toISOString().slice(0, 10), shipment_time: '', ddt_reference: '', notes: '' });
      setDetail(r.data); setDetailOpen(true);
      loadShipments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/shipment-registry/${id}`);
      setDetail(r.data); setDetailOpen(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const reloadDetail = async () => {
    if (!detail) return;
    try {
      const r = await api.get(`/shipment-registry/${detail.id}`);
      setDetail(r.data);
      loadShipments();
    } catch (e) { console.error('ShipmentRegistry: reloadDetail failed', e); }
  };

  const [pendingDelete, setPendingDelete] = useState(null); // shipment being confirmed for deletion

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/shipment-registry/${pendingDelete.id}`);
      toast.success(`Spedizione ${pendingDelete.shipment_number} eliminata`);
      setPendingDelete(null);
      loadShipments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
      setPendingDelete(null);
    }
  };

  const totalShippedAll = shipments.reduce((s, x) => s + (x.total_shipped_quantity || 0), 0);

  return (
    <div className="p-6 space-y-4" data-testid="shipment-registry-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Truck className="w-6 h-6" />
          <h1 className="text-2xl font-bold">Registro Spedizioni</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 flex items-center gap-3">
            <span><Package className="w-3 h-3 inline mr-1" />{shipments.length} spedizioni · {totalShippedAll} pezzi totali</span>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="new-shipment-btn"><Plus className="w-4 h-4 mr-1" />Nuova Spedizione</Button>
        </div>
      </div>

      {/* Filters */}
      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1"><Filter className="w-4 h-4 text-slate-400" /><span className="text-xs font-semibold">Filtri</span></div>
          <div>
            <Label className="text-[10px]">Cliente</Label>
            <Select value={filters.customer_id || '__all'} onValueChange={v => setFilters({ ...filters, customer_id: v === '__all' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs w-40" data-testid="filter-customer"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tutti</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px]">Dal</Label><Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="h-8 text-xs" data-testid="filter-date-from" /></div>
          <div><Label className="text-[10px]">Al</Label><Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="h-8 text-xs" data-testid="filter-date-to" /></div>
          <div>
            <Label className="text-[10px]">Stato</Label>
            <Select value={filters.status || '__all'} onValueChange={v => setFilters({ ...filters, status: v === '__all' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs w-32" data-testid="filter-status"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tutti</SelectItem>
                {['Bozza', 'Confermata', 'Spedita', 'Annullata'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Prodotto</Label>
            <Select value={filters.product_id || '__all'} onValueChange={v => setFilters({ ...filters, product_id: v === '__all' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs w-40" data-testid="filter-product"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tutti</SelectItem>
                {products.slice(0, 200).map(p => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Lotto Cliente</Label>
            <Input value={filters.customer_lot} onChange={e => setFilters({ ...filters, customer_lot: e.target.value })} placeholder="Es. PROD-2026-7845" className="h-8 text-xs w-44" data-testid="filter-customer-lot" />
          </div>
          {Object.values(filters).some(v => v) && <Button size="sm" variant="ghost" onClick={() => setFilters({ customer_id: '', date_from: '', date_to: '', status: '', product_id: '', customer_lot: '' })}>Pulisci</Button>}
        </div>
      </CardContent></Card>

      {/* List table */}
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b"><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
            <th className="py-2 px-3">N°</th>
            <th className="py-2 px-3">Data · Ora</th>
            <th className="py-2 px-3">Cliente</th>
            <th className="py-2 px-3">DDT rif.</th>
            <th className="py-2 px-3 text-right">Righe</th>
            <th className="py-2 px-3 text-right">Q.tà Sped.</th>
            <th className="py-2 px-3">Stato</th>
            <th className="py-2 px-3 w-32"></th>
          </tr></thead>
          <tbody>
            {shipments.length === 0 ? (
              <tr><td colSpan="8" className="py-12 text-center text-slate-400">
                <Truck className="w-8 h-8 mx-auto text-slate-300 mb-2" />Nessuna spedizione. Crea la prima!
              </td></tr>
            ) : shipments.map(s => (
              <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`ship-row-${s.id}`}>
                <td className="py-2 px-3 font-mono text-xs">{s.shipment_number}</td>
                <td className="py-2 px-3">{s.shipment_date}{s.shipment_time ? ` · ${s.shipment_time}` : ''}</td>
                <td className="py-2 px-3">{s.customer_name || '—'}</td>
                <td className="py-2 px-3 font-mono text-xs">{s.ddt_reference || '—'}</td>
                <td className="py-2 px-3 text-right">{s.lines_count}</td>
                <td className="py-2 px-3 text-right font-mono">{s.total_shipped_quantity}</td>
                <td className="py-2 px-3"><Badge className={`text-[10px] border ${STATUS_COLORS[s.status]}`}>{s.status}</Badge></td>
                <td className="py-2 px-3 space-x-1 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(s.id)} title="Dettaglio" data-testid={`ship-view-${s.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                  {(s.status === 'Bozza' || s.status === 'Annullata') && (
                    <Button size="sm" variant="ghost" onClick={() => setPendingDelete(s)} title="Elimina" data-testid={`ship-delete-row-${s.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="new-shipment-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5" />Nuova spedizione</DialogTitle></DialogHeader>
          <form onSubmit={createShipment} className="space-y-3">
            <div>
              <Label>Cliente *</Label>
              <Select value={createForm.customer_id} onValueChange={v => setCreateForm({ ...createForm, customer_id: v })}>
                <SelectTrigger data-testid="new-ship-customer"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Data *</Label><Input type="date" value={createForm.shipment_date} onChange={e => setCreateForm({ ...createForm, shipment_date: e.target.value })} required data-testid="new-ship-date" /></div>
              <div><Label>Ora</Label><Input type="time" value={createForm.shipment_time} onChange={e => setCreateForm({ ...createForm, shipment_time: e.target.value })} /></div>
            </div>
            <div><Label>DDT rif.</Label><Input value={createForm.ddt_reference} onChange={e => setCreateForm({ ...createForm, ddt_reference: e.target.value })} placeholder="Rif. DDT esterno" /></div>
            <div><Label>Note</Label><Textarea rows={2} value={createForm.notes} onChange={e => setCreateForm({ ...createForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="new-ship-submit">Crea e Aggiungi Righe</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <ShipmentDetailDialog open={detailOpen} onOpenChange={setDetailOpen} shipment={detail} onChanged={reloadDetail} onDeleted={() => { setDetailOpen(false); loadShipments(); }} />

      {/* Confirm delete AlertDialog (list-level) */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" />Confermare eliminazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente la spedizione <strong className="font-mono">{pendingDelete?.shipment_number}</strong>
              {pendingDelete?.customer_name ? <> del cliente <strong>{pendingDelete.customer_name}</strong></> : null}.
              <br /><br />
              Questa operazione <strong>non può essere annullata</strong>. Le eventuali righe della spedizione saranno rimosse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">No, mantieni</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-yes">Sì, elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function ShipmentDetailDialog({ open, onOpenChange, shipment, onChanged, onDeleted }) {
  const [customerProducts, setCustomerProducts] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ product_id: '', order_line_id: '', production_lot_id: '', shipped_quantity: '', notes: '' });
  const [overdeliveryConfirm, setOverdeliveryConfirm] = useState(null); // {excess, onConfirm}
  const [editHeader, setEditHeader] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmShipOpen, setConfirmShipOpen] = useState(false);
  const [cancelShipOpen, setCancelShipOpen] = useState(false);
  const [pendingLineDelete, setPendingLineDelete] = useState(null);

  const loadCustomerProducts = useCallback(async () => {
    if (!shipment?.customer_id) return;
    try {
      const r = await api.get(`/shipment-registry/customer-products?customer_id=${shipment.customer_id}`);
      setCustomerProducts(r.data || []);
    } catch (e) { console.error('ShipmentRegistry: loadCustomerProducts failed', e); }
  }, [shipment?.customer_id]);

  const loadOpenOrders = useCallback(async (productId) => {
    if (!shipment?.customer_id) return;
    try {
      const params = new URLSearchParams({ customer_id: shipment.customer_id });
      if (productId) params.append('product_id', productId);
      const r = await api.get(`/shipment-registry/customer-open-orders?${params.toString()}`);
      setOpenOrders(r.data || []);
    } catch (e) { console.error('ShipmentRegistry: loadOpenOrders failed', e); }
  }, [shipment?.customer_id]);

  useEffect(() => {
    if (open && shipment?.status === 'Bozza') loadCustomerProducts();
  }, [open, shipment?.id, shipment?.status, loadCustomerProducts]);

  useEffect(() => {
    if (addForm.product_id) loadOpenOrders(addForm.product_id);
    else setOpenOrders([]);
  }, [addForm.product_id, loadOpenOrders]);

  const resetWizard = () => setAddForm({ product_id: '', order_line_id: '', production_lot_id: '', shipped_quantity: '', notes: '' });

  if (!shipment) return null;
  const isDraft = shipment.status === 'Bozza';
  const isCancelled = shipment.status === 'Annullata';

  const printPdf = async () => {
    try {
      const r = await api.get(`/shipment-registry/${shipment.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore generazione PDF'); }
  };

  const addLine = async (e) => {
    e.preventDefault();
    if (!addForm.product_id || !addForm.order_line_id || !addForm.shipped_quantity) { toast.error('Prodotto, riga ordine e quantità obbligatori'); return; }
    const qty = parseInt(addForm.shipped_quantity);
    const residuo = selectedLine?.remaining_quantity ?? 0;
    // Over-delivery branch: confirm before saving
    if (selectedLine && qty > residuo) {
      const excess = qty - residuo;
      setOverdeliveryConfirm({
        excess,
        onConfirm: async () => {
          setOverdeliveryConfirm(null);
          await doAddLine(qty);
        },
      });
      return;
    }
    await doAddLine(qty);
  };

  const doAddLine = async (qty) => {
    try {
      await api.post(`/shipment-registry/${shipment.id}/lines`, {
        order_line_id: addForm.order_line_id,
        production_lot_id: addForm.production_lot_id || null,
        shipped_quantity: qty,
        notes: addForm.notes || null,
      });
      toast.success('Riga aggiunta');
      setAddOpen(false);
      resetWizard();
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const editLine = async (line, patch) => {
    try {
      await api.patch(`/shipment-registry/${shipment.id}/lines/${line.id}`, patch);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const confirmEditLineQty = (line, newQty) => {
    // If new qty creates (or increases) overdelivery, confirm with user first.
    const residuoLine = line.remaining_before ?? 0;
    if (newQty > residuoLine) {
      const excess = newQty - residuoLine;
      setOverdeliveryConfirm({
        excess,
        onConfirm: async () => {
          setOverdeliveryConfirm(null);
          await editLine(line, { shipped_quantity: newQty });
        },
      });
      return;
    }
    editLine(line, { shipped_quantity: newQty });
  };

  const removeLine = (lineId) => {
    setPendingLineDelete(lineId);
  };

  const doRemoveLine = async () => {
    if (!pendingLineDelete) return;
    try {
      await api.delete(`/shipment-registry/${shipment.id}/lines/${pendingLineDelete}`);
      toast.success('Riga eliminata');
      setPendingLineDelete(null);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
      setPendingLineDelete(null);
    }
  };

  const confirmShip = () => setConfirmShipOpen(true);

  const doConfirmShip = async () => {
    try {
      await api.post(`/shipment-registry/${shipment.id}/confirm`);
      toast.success('Spedizione confermata · Vista Ordini aggiornata');
      setConfirmShipOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore conferma');
      setConfirmShipOpen(false);
    }
  };

  const cancelShip = () => setCancelShipOpen(true);

  const doCancelShip = async () => {
    try {
      await api.post(`/shipment-registry/${shipment.id}/cancel`);
      toast.success('Spedizione annullata · Consegne stornate');
      setCancelShipOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
      setCancelShipOpen(false);
    }
  };

  const markShipped = async () => {
    try {
      await api.post(`/shipment-registry/${shipment.id}/mark-shipped`);
      toast.success('Marcata come Spedita');
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/shipment-registry/${shipment.id}`);
      toast.success(`Spedizione ${shipment.shipment_number} eliminata`);
      setConfirmDeleteOpen(false);
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
      setConfirmDeleteOpen(false);
    }
  };

  const saveHeader = async () => {
    try {
      await api.put(`/shipment-registry/${shipment.id}`, editHeader);
      setEditHeader(null);
      toast.success('Testata aggiornata');
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  // Filter open lines for the add-line dialog
  const selectedOrder = openOrders.find(o => o.open_lines.some(l => l.id === addForm.order_line_id));
  const selectedLine = selectedOrder?.open_lines.find(l => l.id === addForm.order_line_id);
  const availableLots = selectedLine?.production_lots || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="shipment-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Spedizione <span className="font-mono">{shipment.shipment_number}</span>
              <Badge className={`text-[10px] border ${STATUS_COLORS[shipment.status]}`}>{shipment.status}</Badge>
            </DialogTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={printPdf} data-testid="ship-print"><Printer className="w-4 h-4 mr-1" />Stampa PDF</Button>
              {isDraft && <Button size="sm" onClick={confirmShip} disabled={shipment.lines_count === 0} data-testid="ship-confirm"><CheckCircle2 className="w-4 h-4 mr-1" />Conferma</Button>}
              {shipment.status === 'Confermata' && <Button size="sm" onClick={markShipped} data-testid="ship-mark-shipped"><Send className="w-4 h-4 mr-1" />Marca Spedita</Button>}
              {(shipment.status === 'Confermata' || shipment.status === 'Spedita' || isDraft) && !isCancelled &&
                <Button size="sm" variant="outline" onClick={cancelShip} data-testid="ship-cancel"><XCircle className="w-4 h-4 mr-1 text-red-500" />Annulla</Button>
              }
              {isDraft && (
                <Button size="sm" variant="outline" onClick={() => setConfirmDeleteOpen(true)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="ship-delete">
                  <Trash2 className="w-4 h-4 mr-1" />Elimina
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Header */}
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center justify-between">
          <span>Testata</span>
          {isDraft && !editHeader && <Button size="sm" variant="ghost" onClick={() => setEditHeader({ shipment_date: shipment.shipment_date, shipment_time: shipment.shipment_time || '', ddt_reference: shipment.ddt_reference || '', notes: shipment.notes || '' })}><Pencil className="w-3.5 h-3.5" /></Button>}
        </CardTitle></CardHeader>
        <CardContent className="p-3">
          {!editHeader ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><p className="text-[10px] uppercase text-slate-500">Cliente</p><p className="font-medium">{shipment.customer_name}</p></div>
              <div><p className="text-[10px] uppercase text-slate-500">Data · Ora</p><p className="font-mono">{shipment.shipment_date}{shipment.shipment_time ? ` · ${shipment.shipment_time}` : ''}</p></div>
              <div><p className="text-[10px] uppercase text-slate-500">DDT rif.</p><p className="font-mono">{shipment.ddt_reference || '—'}</p></div>
              <div><p className="text-[10px] uppercase text-slate-500">Creata da</p><p>{shipment.created_by || '—'}</p></div>
              {shipment.notes && <div className="col-span-full"><p className="text-[10px] uppercase text-slate-500">Note</p><p className="whitespace-pre-wrap">{shipment.notes}</p></div>}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div><Label className="text-[10px]">Data</Label><Input type="date" value={editHeader.shipment_date} onChange={e => setEditHeader({ ...editHeader, shipment_date: e.target.value })} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Ora</Label><Input type="time" value={editHeader.shipment_time} onChange={e => setEditHeader({ ...editHeader, shipment_time: e.target.value })} className="h-8 text-xs" /></div>
              <div><Label className="text-[10px]">DDT rif.</Label><Input value={editHeader.ddt_reference} onChange={e => setEditHeader({ ...editHeader, ddt_reference: e.target.value })} className="h-8 text-xs" /></div>
              <div className="col-span-full"><Label className="text-[10px]">Note</Label><Textarea rows={2} value={editHeader.notes} onChange={e => setEditHeader({ ...editHeader, notes: e.target.value })} className="text-xs" /></div>
              <div className="col-span-full flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => setEditHeader(null)}>Annulla</Button><Button size="sm" onClick={saveHeader}>Salva</Button></div>
            </div>
          )}
        </CardContent></Card>

        {/* Lines */}
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center justify-between">
          <span>Righe spedizione ({shipment.lines_count}) · Totale: {shipment.total_shipped_quantity} pezzi{shipment.total_overdelivery_quantity > 0 && <> · <span className="text-amber-700 font-semibold" data-testid="total-overdelivery">Sovraconsegna +{shipment.total_overdelivery_quantity}</span></>}</span>
          {isDraft && <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" />Aggiungi riga</Button>}
        </CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead className="bg-slate-50"><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="py-1.5 px-2">Ordine</th>
              <th className="py-1.5 px-2">Prodotto</th>
              <th className="py-1.5 px-2">Data cons.</th>
              <th className="py-1.5 px-2 text-right">Ordinato</th>
              <th className="py-1.5 px-2 text-right">Già ev.</th>
              <th className="py-1.5 px-2 text-right">Res. pre</th>
              <th className="py-1.5 px-2 text-right">Sped.</th>
              <th className="py-1.5 px-2 text-right">Res. post</th>
              <th className="py-1.5 px-2 text-right">Sovracons.</th>
              <th className="py-1.5 px-2">Lotto Cliente</th>
              <th className="py-1.5 px-2">Lotto / Commessa Evotech</th>
              <th className="py-1.5 px-2">Note</th>
              <th className="py-1.5 px-2 w-20"></th>
            </tr></thead>
            <tbody>
              {shipment.lines.length === 0 ? (
                <tr><td colSpan="13" className="py-8 text-center text-slate-400">Nessuna riga. {isDraft && <>Clicca <em>Aggiungi riga</em>.</>}</td></tr>
              ) : shipment.lines.map(l => (
                <tr key={l.id} className="border-b" data-testid={`line-row-${l.id}`}>
                  <td className="py-1.5 px-2 font-mono text-[10px]">{l.order_number || '—'}{l.customer_order_ref && <span className="block text-slate-400">PO {l.customer_order_ref}</span>}</td>
                  <td className="py-1.5 px-2"><span className="font-medium">{l.product_code}</span> <span className="text-slate-400">{l.product_name}</span></td>
                  <td className="py-1.5 px-2 font-mono text-[10px]">{l.delivery_date_required || '—'}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{l.ordered_quantity}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{l.delivered_before}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{l.remaining_before}</td>
                  <td className="py-1.5 px-2 text-right">
                    {isDraft ? (
                      <Input type="number" defaultValue={l.shipped_quantity} onBlur={e => { const v = parseInt(e.target.value); if (!Number.isNaN(v) && v !== l.shipped_quantity && v > 0) confirmEditLineQty(l, v); }} className="h-6 w-20 text-xs text-right font-mono" />
                    ) : <span className="font-mono font-bold">{l.shipped_quantity}</span>}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono ${l.remaining_after === 0 ? 'text-emerald-700 font-bold' : ''}`}>{l.remaining_after}</td>
                  <td className="py-1.5 px-2 text-right">
                    {(() => {
                      const over = Math.max(0, (l.overdelivery_after || 0) - (l.overdelivery_before || 0));
                      return over > 0
                        ? <Badge className="bg-amber-100 text-amber-800 border border-amber-300 font-mono text-[10px]" data-testid={`overdelivery-badge-${l.id}`}>+{over}</Badge>
                        : <span className="text-slate-300">—</span>;
                    })()}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px]" data-testid={`line-customer-lot-${l.id}`}>
                    {l.customer_lot_snapshot
                      ? <span className="font-semibold bg-sky-50 text-sky-900 border border-sky-200 rounded px-1 py-0.5">{l.customer_lot_snapshot}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px]">{l.lot_code || <span className="text-amber-600">⚠ manca</span>}</td>
                  <td className="py-1.5 px-2 text-[10px]">{l.notes || '—'}</td>
                  <td className="py-1.5 px-2 text-right">
                    {isDraft && <Button size="sm" variant="ghost" onClick={() => removeLine(l.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>

        {shipment.status !== 'Bozza' && (
          <p className="text-[10px] text-slate-500 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {shipment.status === 'Annullata' ? 'Spedizione annullata. Le consegne sono state stornate dalla Vista Ordini per Prodotto.' :
             shipment.status === 'Confermata' || shipment.status === 'Spedita' ? 'Le quantità sono state registrate come consegnate e sono visibili nella Vista Ordini per Prodotto.' : ''}
          </p>
        )}

        {/* Add line dialog — 3-step guided wizard: Product → Order line → Qty/Lot/Notes */}
        <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) resetWizard(); }}>
          <DialogContent className="max-w-3xl" data-testid="add-line-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" />Aggiungi riga · Cliente: <span className="font-mono text-sm">{shipment.customer_name}</span></DialogTitle></DialogHeader>
            <form onSubmit={addLine} className="space-y-3">
              {/* Step indicator */}
              <div className="flex items-center gap-1 text-[10px]">
                <StepChip n={1} label="Prodotto" active={!addForm.product_id} done={!!addForm.product_id} />
                <span className="text-slate-300">→</span>
                <StepChip n={2} label="Riga d'ordine" active={addForm.product_id && !addForm.order_line_id} done={!!addForm.order_line_id} disabled={!addForm.product_id} />
                <span className="text-slate-300">→</span>
                <StepChip n={3} label="Quantità" active={addForm.order_line_id && !addForm.shipped_quantity} done={!!addForm.shipped_quantity} disabled={!addForm.order_line_id} />
              </div>

              {/* STEP 1: Product */}
              <div className="rounded-lg border-2 border-blue-100 bg-blue-50/30 p-3 space-y-1">
                <Label className="text-xs font-semibold text-blue-900">STEP 1 · Prodotto *</Label>
                <Select value={addForm.product_id} onValueChange={v => setAddForm({ product_id: v, order_line_id: '', production_lot_id: '', shipped_quantity: '', notes: addForm.notes })}>
                  <SelectTrigger data-testid="add-line-product"><SelectValue placeholder={customerProducts.length ? 'Seleziona prodotto…' : 'Nessun prodotto con ordini aperti per questo cliente'} /></SelectTrigger>
                  <SelectContent>
                    {customerProducts.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="font-mono text-xs">{p.code || '—'}</span> · {p.name || '—'}
                        <span className="ml-2 text-[10px] text-slate-500">({p.open_lines_count} rig · residuo {p.total_remaining})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!customerProducts.length && <p className="text-[10px] text-amber-700 mt-1">⚠️ Nessun ordine aperto per questo cliente. Verifica che ci siano ordini con quantità ancora da consegnare.</p>}
              </div>

              {/* STEP 2: Order line */}
              <div className={`rounded-lg border-2 p-3 space-y-1 ${addForm.product_id ? 'border-purple-100 bg-purple-50/30' : 'border-slate-100 bg-slate-50/30 opacity-50'}`}>
                <Label className="text-xs font-semibold text-purple-900">STEP 2 · Riga d&apos;ordine aperta *</Label>
                <Select value={addForm.order_line_id} onValueChange={v => setAddForm({ ...addForm, order_line_id: v, production_lot_id: '', shipped_quantity: '' })} disabled={!addForm.product_id}>
                  <SelectTrigger data-testid="add-line-order-line"><SelectValue placeholder={!addForm.product_id ? 'Seleziona prima il prodotto' : openOrders.length ? 'Seleziona riga d\'ordine…' : 'Nessuna riga aperta per questa combinazione'} /></SelectTrigger>
                  <SelectContent>
                    {openOrders.flatMap(o => o.open_lines.map(l => (
                      <SelectItem key={l.id} value={l.id}>
                        <span className="font-mono text-xs">{o.order_number}</span>
                        {o.customer_order_ref && <span className="text-slate-400 text-[10px] ml-1">(PO {o.customer_order_ref})</span>}
                        {l.delivery_date_required && <span className="text-[10px] text-slate-500 ml-1">· cons. {l.delivery_date_required}</span>}
                        <span className="ml-2">· Ord.{l.ordered_quantity} · Cons.{l.delivered_quantity} · <strong className="text-blue-700">Res.{l.remaining_quantity}</strong></span>
                      </SelectItem>
                    )))}
                  </SelectContent>
                </Select>
                {selectedLine && selectedOrder && (
                  <div className="mt-2 rounded bg-white border p-2 text-[11px] grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5">
                    <div><span className="text-slate-500">Ordine:</span> <span className="font-mono">{selectedOrder.order_number}</span></div>
                    {selectedOrder.customer_order_ref && <div><span className="text-slate-500">Rif. cliente:</span> <span className="font-mono">{selectedOrder.customer_order_ref}</span></div>}
                    {selectedOrder.order_date && <div><span className="text-slate-500">Data ordine:</span> <span className="font-mono">{selectedOrder.order_date}</span></div>}
                    {selectedLine.delivery_date_required && <div><span className="text-slate-500">Data cons.:</span> <span className="font-mono">{selectedLine.delivery_date_required}</span></div>}
                    <div><span className="text-slate-500">Ordinato:</span> <strong>{selectedLine.ordered_quantity}</strong></div>
                    <div><span className="text-slate-500">Consegnato:</span> <strong>{selectedLine.delivered_quantity}</strong></div>
                    <div><span className="text-slate-500">Residuo:</span> <strong className="text-blue-700">{selectedLine.remaining_quantity}</strong></div>
                    <div data-testid="wizard-customer-lot"><span className="text-slate-500">Lotto Cliente:</span> <span className="font-mono font-semibold">{selectedLine.customer_lot || '—'}</span></div>
                  </div>
                )}
              </div>

              {/* STEP 3: Qty + lot (optional) + notes */}
              <div className={`rounded-lg border-2 p-3 space-y-2 ${addForm.order_line_id ? 'border-emerald-100 bg-emerald-50/30' : 'border-slate-100 bg-slate-50/30 opacity-50'}`}>
                <Label className="text-xs font-semibold text-emerald-900">STEP 3 · Quantità e dettagli</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px]">Quantità spedita *</Label>
                    <Input type="number" min="1" value={addForm.shipped_quantity} onChange={e => setAddForm({ ...addForm, shipped_quantity: e.target.value })} required disabled={!addForm.order_line_id} data-testid="add-line-qty" />
                    {selectedLine && Number(addForm.shipped_quantity) > selectedLine.remaining_quantity && Number(addForm.shipped_quantity) > 0 &&
                      <p className="text-[10px] text-amber-700 mt-0.5 font-medium" data-testid="overdelivery-warning">
                        ⚠️ Attenzione: la quantità spedita supera il residuo ordine di {Number(addForm.shipped_quantity) - selectedLine.remaining_quantity} pezzi. Verrà registrata una sovraconsegna.
                      </p>
                    }
                  </div>
                  <div>
                    <Label className="text-[10px]">Lotto di produzione <span className="text-slate-400">(opzionale in bozza)</span></Label>
                    <Select value={addForm.production_lot_id} onValueChange={v => setAddForm({ ...addForm, production_lot_id: v === '__none' ? '' : v })} disabled={!addForm.order_line_id}>
                      <SelectTrigger data-testid="add-line-lot"><SelectValue placeholder={availableLots.length ? 'Seleziona lotto…' : 'Nessun lotto disponibile'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— Nessun lotto —</SelectItem>
                        {availableLots.map(lot => (
                          <SelectItem key={lot.id} value={lot.id}>{lot.lot_code} · {lot.status} · Q.{lot.quantity} {lot.good_quantity ? `· Buoni ${lot.good_quantity}` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-500 mt-0.5">💡 Il lotto è obbligatorio prima di confermare la spedizione.</p>
                  </div>
                </div>
                <div><Label className="text-[10px]">Note riga</Label><Textarea rows={2} value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} disabled={!addForm.order_line_id} /></div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setAddOpen(false); resetWizard(); }}>Annulla</Button>
                <Button type="submit" disabled={!addForm.product_id || !addForm.order_line_id || !addForm.shipped_quantity} data-testid="add-line-submit">Aggiungi riga</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        {/* Confirm delete AlertDialog (detail-level, only for Bozza) */}
        <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
          <AlertDialogContent data-testid="detail-confirm-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" />Eliminare la spedizione?</AlertDialogTitle>
              <AlertDialogDescription>
                Stai per eliminare la spedizione in bozza <strong className="font-mono">{shipment.shipment_number}</strong>.
                <br /><br />
                Tutte le righe della spedizione verranno rimosse. Questa operazione <strong>non può essere annullata</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="detail-confirm-delete-cancel">No, mantieni</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="detail-confirm-delete-yes">Sì, elimina</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm shipment AlertDialog */}
        <AlertDialog open={confirmShipOpen} onOpenChange={setConfirmShipOpen}>
          <AlertDialogContent data-testid="confirm-ship-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-blue-600" />Confermare la spedizione?</AlertDialogTitle>
              <AlertDialogDescription>
                Stai per confermare la spedizione <strong className="font-mono">{shipment.shipment_number}</strong> con <strong>{shipment.lines_count} righe</strong> per un totale di <strong>{shipment.total_shipped_quantity} pezzi</strong>.
              </AlertDialogDescription>
              <div className="text-sm text-slate-600 mt-2">
                Alla conferma:
                <ul className="list-disc list-inside mt-1 text-xs">
                  <li>Le quantità saranno registrate come <strong>consegnate</strong></li>
                  <li>La <strong>Vista Ordini per Prodotto</strong> verrà aggiornata automaticamente</li>
                  <li>Non sarà più possibile modificare le righe</li>
                </ul>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-ship-cancel">No, resta in bozza</AlertDialogCancel>
              <AlertDialogAction onClick={doConfirmShip} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-ship-yes">Sì, conferma</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cancel shipment AlertDialog */}
        <AlertDialog open={cancelShipOpen} onOpenChange={setCancelShipOpen}>
          <AlertDialogContent data-testid="cancel-ship-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><XCircle className="w-5 h-5 text-red-600" />Annullare la spedizione?</AlertDialogTitle>
              <AlertDialogDescription>
                Stai per annullare la spedizione <strong className="font-mono">{shipment.shipment_number}</strong>.
                {shipment.status === 'Confermata' || shipment.status === 'Spedita' ? (
                  <><br /><br />⚠️ Le quantità consegnate verranno <strong>stornate dalla Vista Ordini per Prodotto</strong>. Il residuo delle righe d&apos;ordine tornerà al valore precedente.</>
                ) : (
                  <><br /><br />La spedizione in bozza verrà marcata come annullata (non verrà eliminata).</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-ship-cancel">No, mantieni attiva</AlertDialogCancel>
              <AlertDialogAction onClick={doCancelShip} className="bg-red-600 hover:bg-red-700" data-testid="cancel-ship-yes">Sì, annulla</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete line AlertDialog */}
        <AlertDialog open={!!pendingLineDelete} onOpenChange={(v) => !v && setPendingLineDelete(null)}>
          <AlertDialogContent data-testid="delete-line-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" />Eliminare questa riga?</AlertDialogTitle>
              <AlertDialogDescription>La riga verrà rimossa dalla spedizione. Puoi ricrearla in qualsiasi momento finché sei in bozza.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-line-cancel">No, mantieni</AlertDialogCancel>
              <AlertDialogAction onClick={doRemoveLine} className="bg-red-600 hover:bg-red-700" data-testid="delete-line-yes">Sì, elimina riga</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Over-delivery confirm AlertDialog */}
        <AlertDialog open={!!overdeliveryConfirm} onOpenChange={(v) => !v && setOverdeliveryConfirm(null)}>
          <AlertDialogContent data-testid="overdelivery-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-600" />Confermare la sovraconsegna?
              </AlertDialogTitle>
              <AlertDialogDescription>
                La spedizione comporta una sovraconsegna di <strong className="text-amber-700">{overdeliveryConfirm?.excess || 0} pezzi</strong>.
                <br /><br />
                Il residuo dell&apos;ordine resterà a <strong>0</strong> e la quantità in eccesso verrà registrata come <strong>sovraconsegna</strong> sulla riga d&apos;ordine. Confermare?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="overdelivery-confirm-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={() => overdeliveryConfirm?.onConfirm?.()} className="bg-amber-600 hover:bg-amber-700" data-testid="overdelivery-confirm-yes">Conferma spedizione</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}


function StepChip({ n, label, active, done, disabled }) {
  const cls = done
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : active
    ? 'bg-blue-100 text-blue-900 border-blue-300 ring-2 ring-blue-200'
    : disabled
    ? 'bg-slate-100 text-slate-400 border-slate-200'
    : 'bg-slate-100 text-slate-700 border-slate-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${cls}`}>
      <span className="font-bold">{done ? '✓' : n}</span>
      <span>{label}</span>
    </span>
  );
}
